import type { Viewer } from '../../viewer/Viewer';
import { resolveBRepGeometryDescriptor, resolveTopologySelectionPick } from '../../utils/feature';
import type { MeasureClosestPointResult } from '../types/measure-distance-types';
import type { MeasureEntityRef } from '../types/measure-types';
import {
    isArcCircleMeasurementApplicable,
    isMeasureArcCircleGeometryKind,
} from '../types/measure-arc-circle-support';
import {
    MeasureArcCircleReferenceMode,
    type MeasureReferencePolicy,
} from '../types/measure-reference-policy-types';
import { MeasureGeometryKind } from '../types/measure-property-types';
import type { MeasureMetricQueryContext } from '../types/measure-property-types';
import {
    computeAntipodalPointOnCircleDescriptor,
    computeCenterToCenterReferencePoint,
    type MeasureCircleDescriptor,
} from '../utils/measureArcCircleReferencePoints';
import {
    deriveMixedCenterToCenterFromMinimum,
    extractMixedMinimumSidePoints,
} from '../utils/measureArcCircleMixedCenterToCenter';
import { deriveMixedMaximumDistanceFromMinimum } from '../utils/measureArcCircleMixedMaximumDistance';
import {
    isDualCircleMeasurementContext,
    isMixedMeasurementAnchorOnFirstEntity,
    resolveMixedMeasurementAnchorPoint,
    tryBuildArcCircleMixedContext,
    type MeasureArcCircleMixedContext,
} from '../utils/measureArcCircleMixedReferencePoints';
import {
    MeasureMetricCalculator,
    resolveArcCircleCenterAtReferencePoint,
    resolveArcCircleDescriptorAtReferencePoint,
    resolveMeasureCircleDescriptorFromContext,
} from './MeasureMetricCalculator';
import { buildMeasureMetricQueryContext } from './MeasureMetricQueryContextBuilder';
import { classifyMeasureGeometry } from './MeasurePropertyResolver';
import { resolveUnboundedGeometryKind } from './MeasureUnboundedPropertyResolver';
import { MeasureClosestPointReferenceStrategy } from './MeasureClosestPointReferenceStrategy';
import { MeasureClosestPointResolver } from './MeasureClosestPointResolver';
import { MeasureFarthestPointReferenceStrategy } from './MeasureFarthestPointReferenceStrategy';

/**
 * 圆弧/圆参考点策略。
 *
 * 职责：
 * 1. 任一方为圆相关几何时按子模式计算（双圆 / 混合几何）；
 * 2. 混合圆心到圆心 / 最大距离由最小距离结果派生，圆侧取自参考点 A 所在拟合圆；
 * 3. 双选几何不满足圆弧/圆专用条件时，强制降级为全局最近点，避免非圆几何误用圆策略。
 */
export class MeasureArcCircleReferenceStrategy {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 度量计算器。 */
    private readonly metricCalculator: MeasureMetricCalculator;

    /** 全局最近点策略。 */
    private readonly closestPointStrategy: MeasureClosestPointReferenceStrategy;

    /** 全局最远点策略。 */
    private readonly farthestPointStrategy: MeasureFarthestPointReferenceStrategy;

    /** 最近点求解器。 */
    private readonly closestPointResolver: MeasureClosestPointResolver;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.metricCalculator = new MeasureMetricCalculator();
        this.closestPointStrategy = new MeasureClosestPointReferenceStrategy(viewer);
        this.farthestPointStrategy = new MeasureFarthestPointReferenceStrategy(viewer);
        this.closestPointResolver = new MeasureClosestPointResolver(viewer);
    }

    /**
     * 按圆弧/圆子策略求解双选参考点。
     *
     * @param firstEntity 先选实体。
     * @param secondEntity 后选实体。
     * @param policy 参考点策略快照。
     * @returns 参考点结果；无法求解时返回 null。
     */
    public resolve(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        policy: MeasureReferencePolicy,
    ): MeasureClosestPointResult | null {
        if (policy.arcCircleMode === MeasureArcCircleReferenceMode.CustomDistance) {
            return null;
        }

        const geometryKindA = this.resolveEntityGeometryKind(firstEntity);
        const geometryKindB = this.resolveEntityGeometryKind(secondEntity);
        if (geometryKindA === null || geometryKindB === null) {
            return this.resolveGeneralFallback(firstEntity, secondEntity, policy.arcCircleMode);
        }

        if (!isArcCircleMeasurementApplicable(geometryKindA, geometryKindB)) {
            return this.closestPointStrategy.resolve(firstEntity, secondEntity);
        }

        const circleA = this.resolveEntityCircleDescriptor(firstEntity);
        const circleB = this.resolveEntityCircleDescriptor(secondEntity);
        const mixedContext = tryBuildArcCircleMixedContext(firstEntity, secondEntity, circleA, circleB);

        let result: MeasureClosestPointResult | null = null;
        switch (policy.arcCircleMode) {
            case MeasureArcCircleReferenceMode.CenterToCenter:
                result = this.resolveCenterToCenter(circleA, circleB, mixedContext);
                break;
            case MeasureArcCircleReferenceMode.MinimumDistance:
                result = this.resolveMinimumDistance(
                    firstEntity,
                    secondEntity,
                    circleA,
                    circleB,
                    mixedContext,
                );
                break;
            case MeasureArcCircleReferenceMode.MaximumDistance:
                result = this.resolveMaximumDistance(
                    firstEntity,
                    secondEntity,
                    circleA,
                    circleB,
                    mixedContext,
                );
                break;
            default:
                result = null;
                break;
        }

        if (result) {
            return result;
        }

        return this.resolveGeneralFallback(firstEntity, secondEntity, policy.arcCircleMode);
    }

    /**
     * 圆弧/圆专用规则无法求解时，降级为全局最近/最远点。
     *
     * @param firstEntity 先选实体。
     * @param secondEntity 后选实体。
     * @param arcCircleMode 圆弧/圆子策略。
     * @returns 降级结果；无法求解时返回 null。
     */
    private resolveGeneralFallback(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        arcCircleMode: MeasureArcCircleReferenceMode,
    ): MeasureClosestPointResult | null {
        if (arcCircleMode === MeasureArcCircleReferenceMode.MaximumDistance) {
            return this.farthestPointStrategy.resolve(firstEntity, secondEntity);
        }

        return this.closestPointStrategy.resolve(firstEntity, secondEntity);
    }

    /**
     * 求解最小距离参考点对。
     */
    private resolveMinimumDistance(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        circleA: MeasureCircleDescriptor | null,
        circleB: MeasureCircleDescriptor | null,
        mixedContext: MeasureArcCircleMixedContext | null,
    ): MeasureClosestPointResult | null {
        if (isDualCircleMeasurementContext(circleA, circleB)) {
            return this.closestPointStrategy.resolve(firstEntity, secondEntity);
        }

        if (mixedContext) {
            return this.resolveMixedPointToCircleEntity(mixedContext);
        }

        return null;
    }

    /**
     * 求解最大距离参考点对。
     */
    private resolveMaximumDistance(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        circleA: MeasureCircleDescriptor | null,
        circleB: MeasureCircleDescriptor | null,
        mixedContext: MeasureArcCircleMixedContext | null,
    ): MeasureClosestPointResult | null {
        if (isDualCircleMeasurementContext(circleA, circleB)) {
            return this.farthestPointStrategy.resolve(firstEntity, secondEntity);
        }

        if (mixedContext) {
            return this.resolveMixedMaximumDistance(mixedContext);
        }

        return null;
    }

    /**
     * 求解中心到中心参考点对。
     */
    private resolveCenterToCenter(
        circleA: MeasureCircleDescriptor | null,
        circleB: MeasureCircleDescriptor | null,
        mixedContext: MeasureArcCircleMixedContext | null,
    ): MeasureClosestPointResult | null {
        if (isDualCircleMeasurementContext(circleA, circleB)) {
            return computeCenterToCenterReferencePoint(circleA!, circleB!);
        }

        if (mixedContext) {
            return this.resolveMixedCenterToCenter(mixedContext);
        }

        return null;
    }

    /**
     * 求解混合几何圆心到圆心：非圆侧 B 沿用最小距离，圆侧取 A 所在圆弧/圆的拟合圆心。
     */
    private resolveMixedCenterToCenter(
        context: MeasureArcCircleMixedContext,
    ): MeasureClosestPointResult | null {
        const minimumResult = this.resolveMixedPointToCircleEntity(context);
        if (!minimumResult) {
            return null;
        }

        const circleSideInputs = this.resolveCircleSideMeasureInputs(context.circleEntity);
        if (!circleSideInputs) {
            return null;
        }

        const { circlePoint } = extractMixedMinimumSidePoints(
            minimumResult,
            context.circleSideIsFirst,
        );

        const circleCenter = resolveArcCircleCenterAtReferencePoint(
            circleSideInputs.measureContext,
            circlePoint,
            circleSideInputs.geometryKind,
        );
        if (!circleCenter) {
            return null;
        }

        return deriveMixedCenterToCenterFromMinimum(
            minimumResult,
            context,
            circleCenter,
        );
    }

    /**
     * 求解混合几何最大距离：非圆侧 B 沿用最小距离，圆侧取 A 在拟合圆上的对径点。
     */
    private resolveMixedMaximumDistance(
        context: MeasureArcCircleMixedContext,
    ): MeasureClosestPointResult | null {
        const minimumResult = this.resolveMixedPointToCircleEntity(context);
        if (!minimumResult) {
            return null;
        }

        const circleSideInputs = this.resolveCircleSideMeasureInputs(context.circleEntity);
        if (!circleSideInputs) {
            return null;
        }

        const { circlePoint } = extractMixedMinimumSidePoints(
            minimumResult,
            context.circleSideIsFirst,
        );

        const circleDescriptor = resolveArcCircleDescriptorAtReferencePoint(
            circleSideInputs.measureContext,
            circlePoint,
            circleSideInputs.geometryKind,
        );
        if (!circleDescriptor) {
            return null;
        }

        const antipodalPoint = computeAntipodalPointOnCircleDescriptor(
            circleDescriptor,
            circlePoint,
        );
        if (!antipodalPoint) {
            return null;
        }

        return deriveMixedMaximumDistanceFromMinimum(
            minimumResult,
            context,
            antipodalPoint,
        );
    }

    /**
     * 解析圆相关侧实体的度量上下文与几何类别。
     */
    private resolveCircleSideMeasureInputs(
        circleEntity: MeasureEntityRef,
    ): { measureContext: MeasureMetricQueryContext; geometryKind: MeasureGeometryKind } | null {
        const geometryKind = this.resolveEntityGeometryKind(circleEntity);
        if (geometryKind === null) {
            return null;
        }

        const sceneObject = this.viewer.findSceneObjectById(circleEntity.objectId);
        if (!sceneObject) {
            return null;
        }

        const topologyPick = resolveTopologySelectionPick(sceneObject);
        const brepDescriptor = resolveBRepGeometryDescriptor(sceneObject);
        return {
            measureContext: buildMeasureMetricQueryContext(sceneObject, topologyPick, brepDescriptor),
            geometryKind,
        };
    }

    /**
     * 求解混合几何下非圆侧锚点与圆/圆弧侧之间的最小距离。
     */
    private resolveMixedPointToCircleEntity(
        context: MeasureArcCircleMixedContext,
    ): MeasureClosestPointResult | null {
        const anchorPoint = resolveMixedMeasurementAnchorPoint(
            context,
            (entityRef, worldPoint) => this.closestPointResolver.resolveClosestPointOnEntityFromWorldPoint(
                entityRef,
                worldPoint,
            ),
        );
        if (!anchorPoint) {
            return null;
        }

        const pointSideIsFirst = isMixedMeasurementAnchorOnFirstEntity(context);
        return this.closestPointResolver.resolveBetweenWorldPointAndEntity(
            anchorPoint,
            context.circleEntity,
            pointSideIsFirst,
        );
    }

    /**
     * 解析测量实体的几何类别。
     */
    private resolveEntityGeometryKind(entityRef: MeasureEntityRef): MeasureGeometryKind | null {
        if (entityRef.unboundedBinding) {
            return resolveUnboundedGeometryKind(entityRef.unboundedBinding);
        }

        const sceneObject = this.viewer.findSceneObjectById(entityRef.objectId);
        if (!sceneObject) {
            return null;
        }

        const topologyPick = resolveTopologySelectionPick(sceneObject);
        const brepDescriptor = resolveBRepGeometryDescriptor(sceneObject);
        const context = buildMeasureMetricQueryContext(sceneObject, topologyPick, brepDescriptor);
        return classifyMeasureGeometry(context, this.metricCalculator);
    }

    /**
     * 解析测量实体的圆几何描述符。
     */
    private resolveEntityCircleDescriptor(
        entityRef: MeasureEntityRef,
    ): MeasureCircleDescriptor | null {
        const geometryKind = this.resolveEntityGeometryKind(entityRef);
        if (geometryKind === null || !isMeasureArcCircleGeometryKind(geometryKind)) {
            return null;
        }

        const sceneObject = this.viewer.findSceneObjectById(entityRef.objectId);
        if (!sceneObject) {
            return null;
        }

        const topologyPick = resolveTopologySelectionPick(sceneObject);
        const brepDescriptor = resolveBRepGeometryDescriptor(sceneObject);
        const context = buildMeasureMetricQueryContext(sceneObject, topologyPick, brepDescriptor);
        return resolveMeasureCircleDescriptorFromContext(context, this.metricCalculator);
    }
}
